/**
 * Is Chat Bar Visible
 *
 * Gates the chat-bar family of entry points — the bar and the corner bubble it
 * collapses into (for a Legacy-UI org, the bubble alone). Every deliberate open
 * clears the verdict rather than consulting it, so no page rule can make chat
 * unreachable: `Gladly.show()` and a launcher click both work on an excluded page.
 *
 * `v2.behavior.displayRules` is the gen-2 fallback every existing V2 org still
 * carries; the CHAT_BAR entry's own rules supersede it wherever both are present.
 * CHAT_BAR is a singleton — sanitizeLaunchers keeps only the first — so this reads
 * the org's one entry rather than arbitrating between rival rule sets.
 *
 * A page no rule decides shows the default launcher, whichever of the two rule sets
 * is read: the host page's override if it names one, else the CHAT_BAR entry's
 * `payload.defaultDisplay`, else the bar.
 */

import { DISPLAY_RULE_TYPE, RULE_TYPES } from '@/domain/configuration';
import {
  type DefaultDisplay,
  DISPLAY_RULE_BUTTON,
  type V2DisplayRule,
  type V2DisplayRules,
} from '@/domain/configurationV2';
import type { V2Launcher, V2LauncherDisplayRules } from '@/launchers/types';
import { findChatBarLauncher } from '@/lib/chatBar/findChatBarLauncher';
import {
  CHAT_BAR_DISPLAY,
  type DisplayRuleVerdict,
  evaluateUrlDisplayRules,
} from '@/lib/displayRules/evaluateUrlDisplayRules';
import { URL_MATCH_TYPES } from '@/lib/displayRules/urlDisplayRuleMatcher';
import { isRecord } from '@/lib/utils/isRecord';

const BEHAVIOR_RULES_ID = 'v2.behavior.displayRules';

// Gen-2 validation checks only that a pattern is a string, so this is where a
// non-compiling one is caught; the matcher would throw and take the whole set with it.
function compiles(pattern: string): boolean {
  try {
    RegExp(pattern, 'gi');
    return true;
  } catch {
    return false;
  }
}

// Gen-2 rules predate validation — drop the unevaluable, don't lose the whole set.
const isEvaluable = (rule: V2DisplayRule): boolean =>
  rule.type === DISPLAY_RULE_TYPE.URL &&
  !!rule.content &&
  URL_MATCH_TYPES.has(rule.content.matchType) &&
  (rule.content.matchType !== RULE_TYPES.REGEX || compiles(rule.content.value));

function toLauncherDisplayRules(displayRules: Partial<V2DisplayRules>): V2LauncherDisplayRules {
  return {
    // showByDefault means "visible unless a rule matches" — HIDE_ON_MATCH exactly.
    mode: (displayRules.showByDefault ?? true) ? 'HIDE_ON_MATCH' : 'SHOW_ON_MATCH',
    // Not `?? []`: the V1-embed path assigns the config unvalidated, and a throw
    // here happens inside init() and fails the whole widget load.
    rules: (Array.isArray(displayRules.rules) ? displayRules.rules : []).filter(isEvaluable).map((rule) => ({
      matchType: rule.content.matchType,
      value: rule.content.value,
      displayDelay: rule.displayDelay,
      // Unvalidated, like everything else on this path; evaluateUrlDisplayRules
      // ignores a choice it does not recognise rather than dropping the rule.
      display: rule.content.display,
    })),
  };
}

export function isChatBarVisible({
  defaultDisplay: override,
  displayRules,
  launchers,
  url,
}: {
  /** `v2.display`, which only the host page's configurationOverride sets. */
  defaultDisplay?: string;
  displayRules?: Partial<V2DisplayRules> | null;
  launchers?: V2Launcher[];
  url?: string;
}): DisplayRuleVerdict {
  const chatBar = findChatBarLauncher(launchers);
  const defaultDisplay = resolveDefaultDisplay(override, chatBar);
  if (chatBar) {
    return evaluateUrlDisplayRules(chatBar.displayRules, { id: `launcher "${chatBar.id}"`, defaultDisplay, url });
  }

  if (!displayRules) {
    return { displayDelay: 0, display: defaultDisplay };
  }

  return evaluateUrlDisplayRules(toLauncherDisplayRules(displayRules), { id: BEHAVIOR_RULES_ID, defaultDisplay, url });
}

// The V1-embed path assigns the config unvalidated, so the override is checked again
// here. The stored value reads as the bar unless it is exactly the button: absent, the
// admin's empty string, and a value from a newer admin all keep today's behavior.
function resolveDefaultDisplay(override: unknown, chatBar: V2Launcher | undefined): DefaultDisplay {
  if (override === CHAT_BAR_DISPLAY.BAR || override === CHAT_BAR_DISPLAY.BUTTON) return override;
  const payload = chatBar?.payload;
  return isRecord(payload) && payload.defaultDisplay === DISPLAY_RULE_BUTTON
    ? CHAT_BAR_DISPLAY.BUTTON
    : CHAT_BAR_DISPLAY.BAR;
}
